import { FC, MouseEvent, ReactElement, ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
	AwardRibbonOutline,
	Box,
	CelebrationStreakOutline,
	ChartBarOutline,
	ChatAlt2Outline,
	ChatAltOutline,
	CogOutline,
	DotsHorizontalOutline,
	HistoryOutline,
	HomeOutline,
	HStack,
	LeftDrawerCollapseOutline,
	LeftDrawerExpandOutline,
	rem,
	ShoppingCartOutline,
	Stack,
	TargetOutline,
	Text,
	Tooltip,
	UsersOutline,
	VStack,
} from '@bonusly/design-system';
import { useBuildMonarchURL } from '@bonusly/url';
import { FeatureFlag, Permission, ProtectedContent, useCurrentCompany, useCurrentUser, useFeatureFlag, usePermission, Variant } from '@bonusly/who-am-i';

import { useMonthlyCelebrationsQuery } from '../../../components/celebrations/celebrations-widget/hooks/use-celebrations-api';
import { Conceal } from '../../../components/display/conceal';
import { SeasonalToggle } from '../../../components/seasonal/seasonal-toggle';
import { useIsRecognize } from '../../../hooks/use-is-recognize';
import { useCelebrationsPanel, useLeaderboardPanel, useNotificationsPanel, useRewardsPanel, useTeamPanel } from '../../../hooks/use-navbar-panels';
import { AnalyticsEvent } from '../../../lib/analytics/events';
import { useAnalytics } from '../../../lib/analytics/use-analytics';
import { loadHighlights } from '../../../queries/rewards';
import { AppPath } from '../../admin-navbar/config/router';
import { useHomepageRedesignEnabled } from '../../bonus-feed/features/recognition/components/BonusFeed/hooks/useHomepageRedesignEnabled';
import { useTeamTracker } from '../../bonus-feed/features/recognition/components/tracker-widget/hooks';
import { useCurrentUserReports } from '../../team-management/hooks/use-current-user-reports';
import { useFetchNotifications } from '../../top-navbar/components/Notification/hooks/useFetchNotifications';
import { useAdminNavLink } from '../hooks/use-admin-nav-link';
import { AnnouncementMenuItem } from './announcement-menu-item';
import { CelebrationsPanel } from './celebrations';
import { CompanyLogo } from './company-logo';
import { CreateNewMenu } from './create-new-menu';
import { DarkModeAlert, DarkModeAlertRef } from './dark-mode-alert';
import { IconAndTextContent } from './icon-and-text-content';
import { LeaderboardPanel } from './leaderboard';
import { NotificationPanel } from './notification';
import { RewardsHighlightsPanel } from './rewards-highlights-panel';
import { SearchNavItem } from './search-nav-item';
import { TeamPanel } from './team/team-panel';
import { TrialCard } from './trial-card';
import { UserMenu } from './user-menu';
import { UserNavAction } from './user-nav-action';
import { UserNavLink } from './user-nav-link';
import { UserNavMenu } from './user-nav-menu';

interface UserNavbarProps {
	isExpanded: boolean;
	toggleSidebar: () => void;
}

export const UserNavbar: FC<UserNavbarProps> = ({ isExpanded, toggleSidebar }) => {
	const { t } = useTranslation('navigation');
	const { t: c } = useTranslation('common');
	const { track } = useAnalytics();
	const isRecognize = useIsRecognize();
	const monarchURL = useBuildMonarchURL();
	const user = useCurrentUser();
	const isAdmin = user.global_admin;
	const { hide_competitive_features: hideCompetitiveFeatures } = useCurrentCompany();
	const hideLeaderboard = hideCompetitiveFeatures && !isAdmin;
	const isHomepageRedesignEnabled = useHomepageRedesignEnabled();
	const isRemoveHomepageWidgetColumnEnabled = useFeatureFlag(Variant.RemoveHomepageWidgetColumn);
	const canAccessTeamHub = usePermission(Permission.CanAccessTeamHub);

	const homeURL = isRecognize ? monarchURL(AppPath.Home) : AppPath.Home;
	const adminNavLink = useAdminNavLink();
	const { unreadCount } = useFetchNotifications(user.locale);
	const { data: directReports = [] } = useCurrentUserReports();

	// Panel states (managed via zustand)
	const notificationsPanel = useNotificationsPanel();
	const teamPanel = useTeamPanel();
	const leaderboardPanel = useLeaderboardPanel();
	const celebrationsPanel = useCelebrationsPanel();
	const rewardsPanel = useRewardsPanel();

	// Celebrations data for the More menu
	const { data: celebrationsData, error: celebrationsError, isLoading: celebrationsLoading } = useMonthlyCelebrationsQuery();
	const { all, relevant } = celebrationsData;
	const hasCelebrations = relevant.recent.length > 0 || relevant.upcoming.length > 0 || all.recent.length > 0 || all.upcoming.length > 0;
	const hideCelebrations = !!celebrationsError || celebrationsLoading || !hasCelebrations;

	// Tracker widget data for Team menu (only fetch when feature flag is enabled)
	// NOTE: Team will show for non-managers if there is team data
	// Team will always show for managers regardless of team data
	const { data: people, isError, isLoading: isLoadingTrackerData } = useTeamTracker({ enabled: isRemoveHomepageWidgetColumnEnabled });
	const hideTeamMenu = isRemoveHomepageWidgetColumnEnabled
		? (isLoadingTrackerData || isError || !people || people.length === 0) && !user.is_manager
		: !canAccessTeamHub;

	// Scrollable nav overflow detection
	const scrollableNavRef = useRef<HTMLDivElement>(null);
	const hasOverflow = useHasOverflow(scrollableNavRef);

	// Dark mode alert ref for dismissing when user menu is opened
	const darkModeAlertRef = useRef<DarkModeAlertRef>(null);

	// Handle click for cross-domain navigation
	const handleLogoClick = (e: MouseEvent<HTMLAnchorElement>) => {
		if (isRecognize) {
			e.preventDefault();
			window.location.href = homeURL;
		}
	};

	const handleRewardsClick = async () => {
		track(AnalyticsEvent.UserNavbar.RewardsNavigation);

		if (rewardsPanel.isOpen) {
			rewardsPanel.close();
		} else {
			await loadHighlights();
			rewardsPanel.open();
		}
	};

	const handleRewardsHover = () => {
		if (!rewardsPanel.isOpen) {
			loadHighlights();
		}
	};

	return (
		<>
			<VStack bgColor="bg.canvas" w="full" h="100%" px={rem(8)} py={rem(20)} spacing={0} align="flex-start">
				<HStack justifyContent="flex-start" height={rem(36)} width="100%" px={rem(10)} mb={rem(24)} spacing={0} position="relative">
					<Box
						as={Link}
						id="primary-nav-logo"
						h={rem(36)}
						to={homeURL}
						onClick={handleLogoClick}
						opacity={isExpanded ? 1 : 0}
						overflow="hidden"
						pointerEvents={isExpanded ? 'auto' : 'none'}
						transition={isExpanded ? 'opacity 0.15s 0.075s linear' : 'opacity 0.15s cubic-bezier(.4,0,.2,1)'}
						flexShrink={0}
					>
						<CompanyLogo />
					</Box>

					<Tooltip label={isExpanded ? t('collapseSidebar') : t('expandSidebar')} placement="right" openDelay={900}>
						<Box
							as="button"
							aria-label={isExpanded ? t('collapseSidebar') : t('expandSidebar')}
							display="flex"
							alignItems="center"
							justifyContent="center"
							p={3}
							borderRadius="md"
							cursor="pointer"
							color="text.softer"
							_hover={{ bg: 'secondary.bgAlpha' }}
							onClick={toggleSidebar}
							position="absolute"
							right={0}
						>
							{isExpanded ? (
								<LeftDrawerCollapseOutline data-testid="collapse-sidebar" size="sm" />
							) : (
								<LeftDrawerExpandOutline data-testid="expand-sidebar" size="sm" />
							)}
						</Box>
					</Tooltip>
				</HStack>

				<Stack as="nav" spacing={2} flexDir="column" width="100%" flex={1} minHeight={0}>
					{/* Scrollable nav items */}
					<Box
						ref={scrollableNavRef}
						flex={1}
						overflowY="auto"
						overflowX="hidden"
						minHeight={0}
						borderBottom={hasOverflow ? '1px solid' : 'none'}
						borderColor="border.default"
						mx={rem(-8)}
						px={rem(8)}
						pb={hasOverflow ? rem(8) : 0}
						sx={{
							'&::-webkit-scrollbar': { width: '6px' },
							'&::-webkit-scrollbar-track': { background: 'transparent' },
							'&::-webkit-scrollbar-thumb': {
								background: 'scrollbar.thumb',
								borderRadius: '3px',
							},
							'&::-webkit-scrollbar-thumb:hover': { background: 'scrollbar.thumbHover' },
							scrollbarColor: 'var(--chakra-colors-scrollbar-thumb) transparent',
							scrollbarWidth: 'thin',
						}}
					>
						<VStack spacing={1} align="stretch">
							{/* ANNOUNCEMENTS */}
							<FeatureFlag flag={Variant.RemoveHomepageWidgetColumn}>
								<ProtectedContent permissions={Permission.CanAccessAnnouncements}>
									<AnnouncementMenuItem isExpanded={isExpanded} />
								</ProtectedContent>
							</FeatureFlag>

							{/* HOME */}
							<UserNavLink
								to="/"
								onClick={() => track(AnalyticsEvent.UserNavbar.HomeNavigation)}
								data-testid="nav-link-home"
								label={t('links.home')}
								isExpanded={isExpanded}
							>
								<IconAndTextContent icon={<HomeOutline size="md" />} label={t('links.home')} isExpanded={isExpanded} />
							</UserNavLink>

							{/* SEARCH */}
							<SearchNavItem isExpanded={isExpanded} />

							{/* 1:1s */}
							<ProtectedContent permissions={Permission.CanAccessOneOnOnes}>
								<UserNavLink
									to="/meet"
									onClick={() => track(AnalyticsEvent.UserNavbar.OneOnOnesNavigation)}
									data-testid="nav-link-one-on-ones"
									label={t('links.oneOnOnes')}
									isExpanded={isExpanded}
								>
									<IconAndTextContent icon={<ChatAlt2Outline size="md" />} label={t('links.oneOnOnes')} isExpanded={isExpanded} />
								</UserNavLink>
							</ProtectedContent>

							{/* GOALS */}
							<ProtectedContent permissions={Permission.CanAccessGoals}>
								<UserNavLink
									to="/growth/goals"
									onClick={() => track(AnalyticsEvent.UserNavbar.GoalsNavigation)}
									data-testid="nav-link-goals"
									label={t('links.goals')}
									isExpanded={isExpanded}
								>
									<IconAndTextContent icon={<TargetOutline size="md" />} label={t('links.goals')} isExpanded={isExpanded} />
								</UserNavLink>
							</ProtectedContent>

							{/* RECAPS */}
							<ProtectedContent permissions={Permission.CanAccessOneOnOnes}>
								<UserNavLink
									to="/growth/recap-review"
									onClick={() => track(AnalyticsEvent.UserNavbar.RecapsNavigation)}
									data-testid="nav-link-recaps"
									label={t('links.recaps')}
									isExpanded={isExpanded}
								>
									<IconAndTextContent icon={<HistoryOutline size="md" />} label={t('links.recaps')} isExpanded={isExpanded} />
								</UserNavLink>
							</ProtectedContent>

							{/* TEAM */}
							<Conceal if={hideTeamMenu}>
								<UserNavAction
									onClick={() => {
										track(AnalyticsEvent.UserNavbar.TeamNavigation, { isManager: user.is_manager });
										teamPanel.toggle();
									}}
									isActive={teamPanel.isOpen}
									data-testid="nav-link-team"
									label={t('links.team')}
									isExpanded={isExpanded}
								>
									<IconAndTextContent icon={<UsersOutline size="md" />} label={t('links.team')} isExpanded={isExpanded} />
								</UserNavAction>
							</Conceal>

							<UserNavAction
								onClick={handleRewardsClick}
								onMouseEnter={handleRewardsHover}
								isActive={rewardsPanel.isOpen}
								data-testid="nav-link-rewards"
								label={t('links.rewards')}
								isExpanded={isExpanded}
							>
								<IconAndChildContent icon={<ShoppingCartOutline size="md" />} isExpanded={isExpanded}>
									<HStack justifyContent="space-between" alignItems="flex-start" width="100%">
										<Text fontSize={100} fontWeight="medium" whiteSpace="nowrap">
											{t('links.rewards')}
										</Text>
										<Box bgColor="points.bgAlpha" fontWeight="semibold" fontSize={1} color="points.fg" borderRadius="full" px={rem(6)} py={rem(3)}>
											+{c('intl_number', { value: user.earning_balance })}
										</Box>
									</HStack>
								</IconAndChildContent>
							</UserNavAction>

							{/* MORE MENU ITEMS */}
							<UserNavMenu buttonIcon={<DotsHorizontalOutline size="md" />} buttonText={t('moreButtonText')} isExpanded={isExpanded}>
								{/* INCENTIVES */}
								<ProtectedContent permissions={Permission.CanAccessIncentives}>
									<UserNavLink to="/incentives" onClick={() => track(AnalyticsEvent.UserNavbar.IncentivesNavigation)} data-testid="nav-link-incentives">
										<IconAndTextContent icon={<AwardRibbonOutline size="md" />} label={t('links.incentives')} isExpanded />
									</UserNavLink>
								</ProtectedContent>
								{/* LEADERBOARD */}
								<ProtectedContent permissions={Permission.CanAccessAnalytics}>
									<Conceal if={hideLeaderboard}>
										{isHomepageRedesignEnabled ? (
											<UserNavAction
												onClick={() => {
													track(AnalyticsEvent.UserNavbar.LeaderboardNavigation);
													leaderboardPanel.toggle();
												}}
												isActive={leaderboardPanel.isOpen}
												data-testid="nav-link-leaderboard"
												label={t('links.leaderboard')}
												isExpanded
											>
												<IconAndTextContent icon={<ChartBarOutline size="md" />} label={t('links.leaderboard')} isExpanded />
											</UserNavAction>
										) : (
											<UserNavLink
												to="/analytics/leaderboards"
												onClick={() => track(AnalyticsEvent.UserNavbar.LeaderboardNavigation)}
												isRecognizeRoute={true}
												data-testid="nav-link-leaderboard"
											>
												<IconAndTextContent icon={<ChartBarOutline size="md" />} label={t('links.leaderboard')} isExpanded />
											</UserNavLink>
										)}
									</Conceal>
								</ProtectedContent>
								{/* CELEBRATIONS */}
								<FeatureFlag flag={Variant.RemoveHomepageWidgetColumn}>
									<Conceal if={hideCelebrations}>
										<UserNavAction
											onClick={() => {
												track(AnalyticsEvent.UserNavbar.CelebrationsNavigation);
												celebrationsPanel.toggle();
											}}
											isActive={celebrationsPanel.isOpen}
											data-testid="nav-action-celebrations"
										>
											<IconAndTextContent icon={<CelebrationStreakOutline size="md" />} label={t('links.celebrations')} isExpanded />
										</UserNavAction>
									</Conceal>
								</FeatureFlag>
								{/* ADVICE (FEEDBACK) */}
								<ProtectedContent permissions={Permission.CanAccessOneOnOnes}>
									<UserNavLink to="/growth/feedback" onClick={() => track(AnalyticsEvent.UserNavbar.AdviceNavigation)} data-testid="nav-link-feedback">
										<IconAndTextContent icon={<ChatAltOutline size="md" />} label={t('links.feedback')} isExpanded />
									</UserNavLink>
								</ProtectedContent>
							</UserNavMenu>

							{/* CREATE NEW MENU */}
							<CreateNewMenu isExpanded={isExpanded} />
						</VStack>
					</Box>

					{/* Fixed bottom items - always visible */}
					<VStack spacing={1} flexShrink={0}>
						{/* SEASONAL THEME TOGGLE */}
						<SeasonalToggle isExpanded={isExpanded} />

						{/* TRIAL CARD */}
						<TrialCard isExpanded={isExpanded} />

						{/* ADMIN MENU */}
						<ProtectedContent permissions={Permission.CanViewAdmin}>
							<UserNavLink
								to={adminNavLink.to}
								isRecognizeRoute={adminNavLink.isRecognizeRoute}
								onClick={() => track(AnalyticsEvent.UserNavbar.AdminNavigation)}
								data-testid="nav-link-admin"
								label={t('links.adminSettings')}
								isExpanded={isExpanded}
							>
								<IconAndTextContent icon={<CogOutline size="md" />} label={t('links.adminSettings')} isExpanded={isExpanded} />
							</UserNavLink>
						</ProtectedContent>

						{/* USER MENU */}
						<Box position="relative" width="100%">
							<DarkModeAlert ref={darkModeAlertRef} isExpanded={isExpanded} />
							<UserMenu
								isExpanded={isExpanded}
								onOpenNotifications={notificationsPanel.open}
								onMenuOpen={() => darkModeAlertRef.current?.dismiss()}
								unreadCount={unreadCount}
							/>
						</Box>
					</VStack>
				</Stack>

				{/* Notification Panel */}
				<NotificationPanel isOpen={notificationsPanel.isOpen} onClose={notificationsPanel.close} isSidebarExpanded={isExpanded} />

				{/* Team Panel */}
				<TeamPanel isOpen={teamPanel.isOpen} onClose={teamPanel.close} isSidebarExpanded={isExpanded} directReports={directReports} people={people} />

				{/* Leaderboard Panel */}
				{isHomepageRedesignEnabled && <LeaderboardPanel isOpen={leaderboardPanel.isOpen} onClose={leaderboardPanel.close} isSidebarExpanded={isExpanded} />}

				{/* Celebrations Panel */}
				<CelebrationsPanel isOpen={celebrationsPanel.isOpen} onClose={celebrationsPanel.close} isSidebarExpanded={isExpanded} />

				<RewardsHighlightsPanel isOpen={rewardsPanel.isOpen} onClose={rewardsPanel.close} isSidebarExpanded={isExpanded} />
			</VStack>
		</>
	);
};

type IconAndChildContentProps = {
	icon: ReactElement;
	isExpanded: boolean;
	children: ReactNode;
};

const IconAndChildContent: FC<IconAndChildContentProps> = ({ children, icon, isExpanded }) => (
	<HStack justifyContent="flex-start" alignItems="flex-start" spacing={3} w="100%">
		{icon}
		<Box opacity={isExpanded ? 1 : 0} transition={isExpanded ? 'opacity 0.15s 0.075s linear' : 'opacity 0.15s cubic-bezier(.4, 0, .2, 1)'} flex={1}>
			{children}
		</Box>
	</HStack>
);

function useHasOverflow(ref: RefObject<HTMLDivElement>) {
	const [hasOverflow, setHasOverflow] = useState(false);

	useEffect(() => {
		const element = ref.current;
		if (!element) return;

		const checkOverflow = () => {
			setHasOverflow(element.scrollHeight > element.clientHeight);
		};

		checkOverflow();

		const resizeObserver = new ResizeObserver(checkOverflow);
		resizeObserver.observe(element);

		return () => resizeObserver.disconnect();
	}, [ref]);

	return hasOverflow;
}
